import { z } from 'zod';
import { cuid, isoDate, paginacao, taxa, valorFinito } from './primitives.js';
import { percentualParaTaxa } from './itens-tributos.js';

export const ipiTratamento = z.enum(['NENHUM', 'CUSTO', 'RECUPERAVEL']);

/**
 * Entrada do motor (acquisitionCost). Taxas em fracao, string decimal.
 * Os switches de recuperavel (switchIcms/Pis/Cofins/IPI) sao campos GRAVADOS, independentes da taxa: so credita (ICMS,
 * PIS, COFINS) ou so tira o IPI do custo (switchIPI) quando ligado. Switch omitido: ICMS/PIS/COFINS = ligado (compat. com
 * quem so envia a taxa); IPI = deduzido de ipiTratamento (RECUPERAVEL = ligado). Regra do IPI: ligado => RECUPERAVEL,
 * desligado => CUSTO (SPEC 9.5.4); se ambos vierem, tem de concordar.
 * acquisitionCost/acquisitionTaxCredit/detalhe/motorVersao sao SAIDA do motor: nao aceitos aqui.
 */
export const criarCustoAquisicaoSchema = z.object({
  itemId: cuid,
  validFrom: isoDate,
  basePrice: valorFinito,
  creditIcms: taxa.optional(),
  creditPis: taxa.optional(),
  creditCofins: taxa.optional(),
  switchIcms: z.boolean().optional(),
  switchPis: z.boolean().optional(),
  switchCofins: z.boolean().optional(),
  switchIPI: z.boolean().optional(),
  ipiRate: taxa.optional(),
  ipiTratamento: ipiTratamento.optional(),
  freightRate: taxa.optional(),
  insuranceRate: taxa.optional(),
  icmsDifRate: taxa.optional(),
  icmsStRate: taxa.optional(),
  /** Cadastro (nao entra no calculo): fornecedor desta vigencia, credito de IBS/CBS (reforma, congelada) e o motivo da mudanca. */
  fornecedorId: cuid.optional(),
  /** Sessao 17: vigencia PARALELA (varios fornecedores vigentes ao mesmo tempo): fecha so a vigente do MESMO fornecedor. Sem isto, fecha todas as abertas do item (comportamento anterior). */
  paralela: z.boolean().optional(),
  /** Sessao 17: marca como a aquisicao preferencial do item (desmarca as demais vigentes). */
  preferencial: z.boolean().optional(),
  creditoIbs: z.boolean().optional(),
  creditoCbs: z.boolean().optional(),
  motivo: z.string().trim().min(1, 'Informe o motivo').max(300, 'O motivo tem no máximo 300 caracteres').optional(),
});
export type CriarCustoAquisicaoDto = z.infer<typeof criarCustoAquisicaoSchema>;

/**
 * POST /custos-aquisicao/simular: calculo ao vivo (nao grava, nao enfileira lote). Mesma entrada do POST, sem `itemId` nem
 * `validFrom` (nada e gravado) e sem os campos de cadastro; `ipiTratamento`/`switchIPI` seguem as mesmas regras.
 */
export const simularCustoAquisicaoSchema = criarCustoAquisicaoSchema.omit({
  itemId: true,
  validFrom: true,
  fornecedorId: true,
  creditoIbs: true,
  creditoCbs: true,
  motivo: true,
});
export type SimularCustoAquisicaoDto = z.infer<typeof simularCustoAquisicaoSchema>;

export interface SimularCustoAquisicaoResponse {
  acquisitionCost: string;
  acquisitionTaxCredit: string;
  /** Saida do motor: creditos e acrescimos com base, taxa (fracao) e valor. */
  detalhe: DetalheCustoAquisicao;
  /** Switches efetivos (os informados ou os padroes) e o tratamento de IPI deduzido. */
  switchIcms: boolean;
  switchPis: boolean;
  switchCofins: boolean;
  switchIPI: boolean;
  ipiTratamento: z.infer<typeof ipiTratamento>;
  motorVersao: string;
}

export const listarCustosAquisicaoSchema = paginacao.extend({ itemId: cuid });
export type ListarCustosAquisicaoQuery = z.infer<typeof listarCustosAquisicaoSchema>;

export interface CustoAquisicaoResponse {
  id: string;
  itemId: string;
  validFrom: string;
  validTo: string | null;
  basePrice: string;
  creditIcms: string;
  creditPis: string;
  creditCofins: string;
  switchIcms: boolean;
  switchPis: boolean;
  switchCofins: boolean;
  switchIPI: boolean;
  ipiRate: string;
  ipiTratamento: z.infer<typeof ipiTratamento>;
  freightRate: string;
  insuranceRate: string;
  icmsDifRate: string;
  icmsStRate: string;
  acquisitionTaxCredit: string;
  acquisitionCost: string;
  detalhe: unknown;
  motorVersao: string;
  createdAt: string;
  /** Fornecedor da vigencia (null = nao informado). Sempre presente na resposta da API. */
  fornecedorId?: string | null;
  fornecedorNome?: string | null;
  preferencial?: boolean;
  /** Credito de IBS/CBS: so cadastro, fora do calculo. Sempre presente na resposta da API. */
  creditoIbs?: boolean;
  creditoCbs?: boolean;
  motivo?: string | null;
  criadoPorId?: string | null;
}

/**
 * `detalhe` do custo de aquisicao = saida do motor (acquisitionCost) gravada junto: creditos e acrescimos com base,
 * taxa (fracao) e valor, tudo string decimal. A tela so exibe; a composicao nunca e recalculada no front.
 */
const creditoDetalhe = z.object({ tax: z.string(), base: z.string(), rate: z.string(), value: z.string() });
const acrescimoDetalhe = z.object({ kind: z.string(), base: z.string(), rate: z.string(), value: z.string() });
export const detalheCustoAquisicaoSchema = z.object({
  credits: z.array(creditoDetalhe).default([]),
  additions: z.array(acrescimoDetalhe).default([]),
  acquisitionTaxCreditByTax: z.record(z.string(), z.string()).default({}),
});
export type DetalheCustoAquisicao = z.infer<typeof detalheCustoAquisicaoSchema>;

/**
 * Dinheiro digitado ("25,50", "1.234,5", "25.5") para a string decimal da API ("25.50", "1234.5"). So reescreve caracteres
 * (nenhuma conta). Invalido ou negativo: null.
 */
export function dinheiroDigitado(texto: string): string | null {
  const t = texto.trim().replace(/^R\$\s*/, '');
  if (!t) return null;
  const normal = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : /^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, '') : t;
  return /^\d+(\.\d+)?$/.test(normal) ? normal : null;
}

const precoBaseCampo = z
  .string()
  .trim()
  .refine((v) => {
    const d = dinheiroDigitado(v);
    return d !== null && valorFinito.safeParse(d).success;
  }, 'Informe o preço base (ex.: 25,50; até 6 casas decimais)');
const percentualCampo = z
  .string()
  .trim()
  .refine((v) => v === '' || percentualParaTaxa(v) !== null, 'Percentual inválido (0 a 100, até 6 casas)');

/**
 * Formulario "nova vigencia de custo de aquisicao" (tela do item): precos e percentuais como o usuario digita. A saida separa
 * o que vai para o CALCULO (`POST /custos-aquisicao/simular`, mesmo corpo do POST sem itemId/validFrom) do que e CADASTRO
 * (vigencia, fornecedor, IBS/CBS, motivo: nada disso entra no calculo). Switches: `true` = recuperavel (ICMS/PIS/COFINS
 * creditam; IPI sai do custo). Percentual vazio = taxa omitida (a API usa 0).
 */
export const custoAquisicaoFormSchema = z
  .object({
    validFrom: isoDate,
    fornecedorId: z.string().trim().optional(),
    basePrice: precoBaseCampo,
    creditIcms: percentualCampo,
    switchIcms: z.boolean(),
    creditPis: percentualCampo,
    switchPis: z.boolean(),
    creditCofins: percentualCampo,
    switchCofins: z.boolean(),
    ipiRate: percentualCampo,
    switchIPI: z.boolean(),
    icmsDifRate: percentualCampo,
    icmsStRate: percentualCampo,
    freightRate: percentualCampo,
    insuranceRate: percentualCampo,
    creditoIbs: z.boolean(),
    creditoCbs: z.boolean(),
    motivo: z.string().trim().max(300, 'O motivo tem no máximo 300 caracteres'),
  })
  .transform((v) => {
    const calculo: SimularCustoAquisicaoDto = {
      basePrice: dinheiroDigitado(v.basePrice) ?? '0',
      switchIcms: v.switchIcms,
      switchPis: v.switchPis,
      switchCofins: v.switchCofins,
      switchIPI: v.switchIPI,
      ...(v.creditIcms ? { creditIcms: percentualParaTaxa(v.creditIcms) ?? '0' } : {}),
      ...(v.creditPis ? { creditPis: percentualParaTaxa(v.creditPis) ?? '0' } : {}),
      ...(v.creditCofins ? { creditCofins: percentualParaTaxa(v.creditCofins) ?? '0' } : {}),
      ...(v.ipiRate ? { ipiRate: percentualParaTaxa(v.ipiRate) ?? '0' } : {}),
      ...(v.icmsDifRate ? { icmsDifRate: percentualParaTaxa(v.icmsDifRate) ?? '0' } : {}),
      ...(v.icmsStRate ? { icmsStRate: percentualParaTaxa(v.icmsStRate) ?? '0' } : {}),
      ...(v.freightRate ? { freightRate: percentualParaTaxa(v.freightRate) ?? '0' } : {}),
      ...(v.insuranceRate ? { insuranceRate: percentualParaTaxa(v.insuranceRate) ?? '0' } : {}),
    };
    const cadastro = {
      validFrom: v.validFrom,
      creditoIbs: v.creditoIbs,
      creditoCbs: v.creditoCbs,
      ...(v.fornecedorId ? { fornecedorId: v.fornecedorId } : {}),
      ...(v.motivo ? { motivo: v.motivo } : {}),
    };
    return { calculo, cadastro };
  });
export type CustoAquisicaoFormEntrada = z.input<typeof custoAquisicaoFormSchema>;
export type CustoAquisicaoFormSaida = z.output<typeof custoAquisicaoFormSchema>;
